/**
 * Page order (design manual end-state workflow + call changes):
 * Welcome → Checklist → Important → Business Entity Setup → Entity dashboard
 *   ↳ per entity: States → DRLP → Individual Licensees → State Login Credentials → back to dashboard
 * → Contacts → Attestation → OTR → Review & Submit → Congratulations / Next Steps
 * Implementation Tasks (signature, authorization forms) were moved to Next Steps only, per Teri (Oct 7).
 */
import type { ClientStepId, EntitySectionId, OnboardingState, WizardLocation } from '../types/onboarding'
import {
  ENTITY_SECTIONS,
  allEntitiesSectionScore,
  clientStepProgress,
} from '../lib/completion'

export const CLIENT_STEP_ORDER: ClientStepId[] = [
  'welcome',
  'checklist',
  'important',
  'entitySetup',
  'entityDashboard',
  'contacts',
  'attestation',
  'otr',
  'review',
  'complete',
]

export function clientLocation(step: ClientStepId): WizardLocation {
  return { kind: 'client', step }
}

export function entityLocation(entityId: string, section: EntitySectionId = 'states'): WizardLocation {
  return { kind: 'entity', entityId, section }
}

export function nextLocation(loc: WizardLocation): WizardLocation {
  if (loc.kind === 'entity') {
    const idx = ENTITY_SECTIONS.indexOf(loc.section)
    if (idx < ENTITY_SECTIONS.length - 1) return entityLocation(loc.entityId, ENTITY_SECTIONS[idx + 1])
    // Completing an entity returns to the dashboard.
    return clientLocation('entityDashboard')
  }
  const idx = CLIENT_STEP_ORDER.indexOf(loc.step)
  return clientLocation(CLIENT_STEP_ORDER[Math.min(idx + 1, CLIENT_STEP_ORDER.length - 1)])
}

export function prevLocation(loc: WizardLocation): WizardLocation | null {
  if (loc.kind === 'entity') {
    const idx = ENTITY_SECTIONS.indexOf(loc.section)
    return idx > 0 ? entityLocation(loc.entityId, ENTITY_SECTIONS[idx - 1]) : clientLocation('entityDashboard')
  }
  const idx = CLIENT_STEP_ORDER.indexOf(loc.step)
  return idx > 0 ? clientLocation(CLIENT_STEP_ORDER[idx - 1]) : null
}

/** Progress bar (design manual "Global Navigation"), with the call's per-entity States screen added. */
export interface ProgressPhase {
  id: string
  label: string
  step?: ClientStepId
  section?: EntitySectionId
}

export const PROGRESS_PHASES: ProgressPhase[] = [
  { id: 'welcome', label: 'Welcome', step: 'welcome' },
  { id: 'checklist', label: 'Checklist', step: 'checklist' },
  { id: 'important', label: 'Important Info', step: 'important' },
  { id: 'entities', label: 'Entities', step: 'entitySetup' },
  { id: 'states', label: 'States', section: 'states' },
  { id: 'drlp', label: 'DRLPs', section: 'drlp' },
  { id: 'individuals', label: 'Individuals', section: 'individuals' },
  { id: 'credentials', label: 'Credentials', section: 'credentials' },
  { id: 'contacts', label: 'Contacts', step: 'contacts' },
  { id: 'attestation', label: 'Attestation', step: 'attestation' },
  { id: 'otr', label: 'OTR Test Report', step: 'otr' },
  { id: 'review', label: 'Review', step: 'review' },
  { id: 'submit', label: 'Submit', step: 'complete' },
]

export function phaseScore(state: OnboardingState, phase: ProgressPhase): number {
  if (phase.section) return allEntitiesSectionScore(state, phase.section)
  if (phase.step) return clientStepProgress(state, phase.step).score
  return 0
}

export function activePhaseId(loc: WizardLocation): string {
  if (loc.kind === 'entity') return PROGRESS_PHASES.find((p) => p.section === loc.section)?.id ?? 'states'
  if (loc.step === 'entityDashboard') return 'entities'
  return PROGRESS_PHASES.find((p) => p.step === loc.step)?.id ?? 'welcome'
}

export function overallPercent(state: OnboardingState): number {
  const total = PROGRESS_PHASES.reduce((sum, p) => sum + phaseScore(state, p), 0)
  return Math.round((total / PROGRESS_PHASES.length) * 100)
}
